'use client';

import { memo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  Position,
  useReactFlow,
  type EdgeProps,
  type Node,
} from '@xyflow/react';
import { Box, token } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import type {
  AttackPathEventEdgeData,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';
import {
  ATTACK_PATH_GRAPH_EDGE_COLOR,
  resolveAttackPathEventNodeColor,
} from './attack-path-event-visuals';
import { useAttackPathCampaignHover } from './attack-path-campaign-hover-context';

const ENERGY_STROKE = token('colors.fg.default');
const SELECTED_STROKE = token('colors.border.accent.focus');

const AttackPathEventEdgeComponent = ({
  id,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  selected,
}: EdgeProps) => {
  const { getNode } = useReactFlow<Node<AttackPathEventNodeData>>();
  const campaignHover = useAttackPathCampaignHover();
  const edgeData = (data ?? {}) as AttackPathEventEdgeData;
  const targetNode = getNode(target);
  const targetColor = targetNode
    ? resolveAttackPathEventNodeColor(targetNode.data)
    : ATTACK_PATH_GRAPH_EDGE_COLOR;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition: sourcePosition ?? Position.Right,
    targetX,
    targetY,
    targetPosition: targetPosition ?? Position.Left,
    curvature: 0.35,
  });
  const isHidden = Boolean(edgeData.hidden);
  const isHoverLit =
    campaignHover.campaignId != null &&
    edgeData.campaignId === campaignHover.campaignId;
  const hoverDimmed = campaignHover.campaignId != null && !isHoverLit;
  const isDimmed = Boolean(edgeData.dimmed || hoverDimmed);
  const opacity = isHidden ? 0 : isDimmed ? 0.18 : 1;
  const isImpactPath = Boolean(edgeData.impactPath);
  const edgeStroke =
    selected || edgeData.pathInkActive
      ? SELECTED_STROKE
      : edgeData.status === 'FAILED'
        ? token('colors.fg.error')
        : edgeData.status === 'IN_PROGRESS'
          ? token('colors.border.accent.default')
          : targetColor;
  const isHypothesized = edgeData.variant === 'hypothesized';
  const isRunning = Boolean(edgeData.isCampaignRunning);
  const showEnergy =
    !isHidden && !isDimmed && !isHypothesized && edgeData.energyActive;
  const showDashFlow =
    isHypothesized && !isHidden && !isDimmed && !edgeData.drawActive;
  const showDraw = !isHidden && edgeData.drawActive;
  const strokeWidth =
    selected || edgeData.pathInkActive || isImpactPath
      ? 2.5
      : edgeData.highlighted || edgeData.spotlightActive
        ? 2.25
        : 2;
  const showLabel = Boolean(
    selected ||
      isHoverLit ||
      edgeData.highlighted ||
      edgeData.spotlightActive ||
      edgeData.pathInkActive,
  );
  const edgeLength = Math.hypot(targetX - sourceX, targetY - sourceY);
  const energyDash =
    edgeLength < 140
      ? '0.28 0.72'
      : edgeLength < 220
        ? '0.18 0.82'
        : '0.12 0.88';

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: edgeStroke,
          strokeWidth,
          opacity,
          strokeDasharray:
            isHypothesized || edgeData.status === 'FAILED' ? '6 10' : undefined,
          ...(showDashFlow
            ? {
                animation: isRunning
                  ? 'attack-path-edge-dash-flow 0.7s linear infinite'
                  : 'attack-path-edge-dash-flow 1.25s linear infinite',
              }
            : {}),
        }}
        interactionWidth={18}
      />
      {showDraw ? (
        <path
          d={edgePath}
          fill="none"
          pathLength={1}
          stroke={SELECTED_STROKE}
          strokeWidth={strokeWidth + 0.25}
          strokeLinecap="round"
          opacity={0.9}
          style={{
            pointerEvents: 'none',
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: 'attack-path-edge-draw 0.45s ease-out forwards',
          }}
        />
      ) : null}
      {showEnergy ? (
        <path
          d={edgePath}
          fill="none"
          pathLength={1}
          stroke={ENERGY_STROKE}
          strokeWidth={
            selected || edgeData.highlighted || isImpactPath ? 2 : 1.5
          }
          strokeLinecap="round"
          strokeDasharray={energyDash}
          opacity={0.55}
          style={{
            pointerEvents: 'none',
            animation: 'attack-path-edge-energy 1.4s linear infinite',
          }}
        />
      ) : null}
      {edgeData.label && !isHidden && showLabel ? (
        <EdgeLabelRenderer>
          <Box
            position="absolute"
            pointerEvents="none"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              opacity,
            }}
          >
            <Box
              px="2"
              py="0.5"
              borderRadius="sm"
              bg="bg.default"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <MetaDefault color="fg.muted" truncate maxW="160px">
                {edgeData.label}
              </MetaDefault>
            </Box>
          </Box>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
};

export const AttackPathEventEdge = memo(AttackPathEventEdgeComponent);
AttackPathEventEdge.displayName = 'AttackPathEventEdge';
